import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchCommunityPackage, submitComment, submitRating } from './communityApi';
import './App.css';

function TrustBadge({ trustTier }) {
  const icons = { official: '\u2605', verified: '\u2713', community: '\u25CB' };
  return <span className={`trust-badge trust-${trustTier}`}>{icons[trustTier] || ''} {trustTier}</span>;
}

export default function CommunityPackagePage() {
  const { packageId } = useParams();
  const [pkg, setPkg] = useState(null);
  const [commentBody, setCommentBody] = useState('');

  useEffect(() => {
    fetchCommunityPackage(packageId).then(setPkg);
  }, [packageId]);

  if (!pkg) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', paddingTop: 120 }}>
        <p style={{ color: '#64748b' }}>Loading package...</p>
      </div>
    );
  }

  const latestVersion = pkg.versions?.[0]?.version ?? pkg.latestVersion;
  const permissions = pkg.versions?.[0]?.manifest?.permissions ?? [];
  const latestNotes = pkg.versions?.[0]?.notes;

  async function handleRate(rating) {
    await submitRating(pkg.packageId, rating);
    setPkg((c) => ({
      ...c,
      ratingCount: (c.ratingCount || 0) + 1,
      averageRating: Number(((((c.averageRating || 0) * (c.ratingCount || 0)) + rating) / ((c.ratingCount || 0) + 1)).toFixed(2))
    }));
  }

  async function handleComment(e) {
    e.preventDefault();
    if (!commentBody.trim()) return;
    const response = await submitComment(pkg.packageId, commentBody);
    if (response?.comment) {
      setPkg((c) => ({ ...c, comments: [response.comment, ...(c.comments ?? [])] }));
      setCommentBody('');
    }
  }

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      {/* Back link */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 24px 0' }}>
        <Link to="/community" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.85rem' }}>
          &larr; Back to Community Hub
        </Link>
      </div>

      {/* Header */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <TrustBadge trustTier={pkg.trustTier} />
          <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>{pkg.type}</span>
        </div>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#f8fafc', margin: '0 0 8px' }}>{pkg.name}</h1>
        <p style={{ fontSize: '1rem', color: '#94a3b8', lineHeight: 1.6, margin: '0 0 20px' }}>{pkg.description}</p>

        {/* Meta grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 32 }}>
          <MetaCard label="Publisher" value={pkg.publisher?.displayName} />
          <MetaCard label="Version" value={`v${latestVersion}`} />
          <MetaCard label="Type" value={pkg.type} />
          <MetaCard label="Platform" value={(pkg.targets || []).join(', ')} />
        </div>
      </section>

      {/* Two columns: details + sidebar */}
      <section className="detail-two-col" style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px 48px' }}>
        {/* Left: details */}
        <div>
          {/* Version notes */}
          {latestNotes && (
            <div className="mp-card" style={{ padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 8px' }}>What's New in v{latestVersion}</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', lineHeight: 1.6, margin: 0 }}>{latestNotes}</p>
            </div>
          )}

          {/* Permissions */}
          {permissions.length > 0 && (
            <div className="mp-card" style={{ padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 12px' }}>Permissions</h3>
              {permissions.map((p) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
                  <div>
                    <strong style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{p.id}</strong>
                    <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '2px 0 0' }}>{p.reason}</p>
                  </div>
                  <span style={{ fontSize: '0.7rem', color: p.required ? '#f59e0b' : '#64748b', textTransform: 'uppercase', flexShrink: 0, marginLeft: 12 }}>
                    {p.required ? 'required' : 'optional'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Tags */}
          <div className="mp-card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 10px' }}>Tags</h3>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(pkg.tags || []).map((tag) => (
                <span key={tag} style={{ background: '#334155', color: '#94a3b8', padding: '4px 10px', borderRadius: 4, fontSize: '0.75rem' }}>{tag}</span>
              ))}
            </div>
          </div>

          {/* All versions */}
          {pkg.versions && pkg.versions.length > 1 && (
            <div className="mp-card" style={{ padding: 20 }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 12px' }}>Version History</h3>
              {pkg.versions.map((v) => (
                <div key={v.version} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
                  <div>
                    <strong style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>v{v.version}</strong>
                    {v.notes && <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '2px 0 0' }}>{v.notes}</p>}
                  </div>
                  <span style={{ color: '#475569', fontSize: '0.78rem', flexShrink: 0, marginLeft: 12 }}>
                    {new Date(v.publishedAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: sidebar */}
        <div>
          {/* Install card */}
          <div className="mp-card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 8px' }}>Install</h3>
            <p style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.5, margin: '0 0 12px' }}>
              Install this extension into mChatAI+ from the Community Hub in the app.
            </p>
            {pkg.repositoryURL && (
              <a href={pkg.repositoryURL} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', padding: '8px 16px', background: '#334155', color: '#e2e8f0', borderRadius: 6, textDecoration: 'none', fontSize: '0.85rem', marginBottom: 8 }}>
                View source
              </a>
            )}
          </div>

          {/* Rating */}
          <div className="mp-card" style={{ padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 10px' }}>Rate this extension</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => handleRate(n)}
                  style={{ flex: 1, padding: '6px 0', background: '#1e293b', border: '1px solid #334155', borderRadius: 4, color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem' }}>
                  {n}
                </button>
              ))}
            </div>
            {(pkg.ratingCount || 0) > 0 && (
              <p style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 8, textAlign: 'center' }}>
                {(pkg.averageRating || 0).toFixed(1)} / 5 ({pkg.ratingCount} ratings)
              </p>
            )}
          </div>

          {/* Comments */}
          <div className="mp-card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 10px' }}>Comments</h3>
            <form onSubmit={handleComment}>
              <textarea
                placeholder="Share your experience..."
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                style={{ width: '100%', padding: 10, background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: '0.85rem', minHeight: 60, resize: 'vertical', boxSizing: 'border-box' }}
              />
              <button type="submit" className="mp-cta-button" style={{ width: '100%', marginTop: 8, padding: '8px 0', fontSize: '0.85rem', textAlign: 'center' }}>
                Post
              </button>
            </form>
            {(pkg.comments ?? []).map((c) => (
              <div key={c.commentId} style={{ borderTop: '1px solid #1e293b', paddingTop: 10, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <strong style={{ color: '#e2e8f0' }}>{c.authorHandle}</strong>
                  <span style={{ color: '#475569' }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '4px 0 0', lineHeight: 1.5 }}>{c.body}</p>
              </div>
            ))}
            {(pkg.comments ?? []).length === 0 && (
              <p style={{ color: '#475569', fontSize: '0.8rem', marginTop: 10 }}>No comments yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="mp-card" style={{ padding: '12px 16px' }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0' }}>{value}</div>
    </div>
  );
}
